import type { AnsweredQuestion, ChatMessageData } from '@elara/domain/models/ChatMessageModelSpec';
import type { ClientMessagePart, MessagePart } from '@elara/domain/models/schemas/message-schemas';
import type { ModelToClientPipeline } from '@elara/domain/processing/pipelines';
import type { InputOf } from '@elara/firebolt/shared';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';

import { batch } from '@preact/signals-react';

import { uuid } from '@elara/core/utils';
import { ChatMessageModelSpec, isAnsweredQuestionsPart } from '@elara/domain/models/ChatMessageModelSpec';
import { extractText, isTextPart, isToolRequestPart } from '@elara/domain/models/schemas/message-schemas';

import { createClientModel } from '#firebase/createClientModel.js';

const TextPartsSeparator = ' ';
const CanAddSeparatorAfterRegEx = /[.!?;:]\s*$/;

/**
 * Upper bound on how long a reply's persisted `generating` flag is trusted. Beyond this we assume
 * the generating instance died mid-stream (OOM/SIGKILL) without clearing the flag, so we stop
 * showing the reply as in-progress. Set comfortably above the longest expected generation.
 */
const STALE_GENERATION_MS = 5 * 60_000;

/**
 * TChatMessage model for client-side usage
 * Provides reactive access to chat messages with the correct path structure
 */
export function createChatMessageModel(firestore: FirestoreContainer, pipeline: ModelToClientPipeline) {
  const generationExpiryTimers = new WeakMap<object, { expiresAt: number; timer: ReturnType<typeof setTimeout> }>();
  const model = createClientModel(
    firestore,
    ChatMessageModelSpec,
    () => ({
      /** Check if this message is from a user (not AI) */
      get isUserMessage(): boolean {
        return this.role === 'user';
      },

      /** Check if this message is from the AI model */
      get isModelMessage(): boolean {
        return this.role === 'model';
      },

      /** Check if this is an event message (e.g. trip dates changed via UI) */
      get isEventMessage(): boolean {
        const meta = this.metadata as Record<string, unknown> | undefined;
        return typeof meta?.event === 'string';
      },

      /**
       * Defers Firestore content while the SSE consumer owns an optimistic model reply.
       *
       * `FirestoreCache` keys instances by document path, so the optimistic reply and every
       * Firestore snapshot for that reply are the same object. The final persisted snapshot can
       * arrive while buffered SSE chunks are still being consumed. Hydrating that snapshot
       * immediately and then appending the remaining chunks duplicates the end of the response.
       * Empty placeholder/stop snapshots can conversely erase content already streamed locally.
       *
       * Keep the newest persisted content aside until `settleStreamingContent()` runs after the SSE
       * loop. Other fields still hydrate immediately, so generating/stopped metadata stays live.
       */
      _hydrate(data: Partial<ChatMessageData>): void {
        const filtered = { ...data };
        if (this.isModelMessage && this.isOptimistic && Array.isArray(filtered.content)) {
          this.__pendingHydratedContent = filtered.content;
          delete filtered.content;
        }
        const baseHydrate = (Object.getPrototypeOf(this) as { _hydrate?(data: Partial<ChatMessageData>): void })
          ._hydrate;
        baseHydrate?.call(this, filtered);
      },

      /**
       * Ends local ownership of streamed content and reconciles an early Firestore snapshot once.
       * A non-empty persisted payload is authoritative. An empty placeholder is ignored when local
       * chunks exist because the server's final `persist()` snapshot may still be in flight.
       */
      settleStreamingContent(): void {
        const persistedContent = this.__pendingHydratedContent;
        this.__pendingHydratedContent = undefined;
        if (persistedContent && (persistedContent.length > 0 || this.content.length === 0)) {
          const baseHydrate = (Object.getPrototypeOf(this) as { _hydrate?(data: Partial<ChatMessageData>): void })
            ._hydrate;
          baseHydrate?.call(this, { content: persistedContent });
        }
        this.__optimisticState = undefined;
      },

      /**
       * Persisted user feedback for this message, if any.
       * Stored under metadata.feedback on the server.
       */
      get feedback():
        | {
            rating: 'good' | 'bad';
            comment?: string;
            userId?: string;
            createdAt?: string;
          }
        | undefined {
        const metadata = this.metadata as unknown;
        if (!metadata || typeof metadata !== 'object') return undefined;
        const feedback = (metadata as { feedback?: unknown }).feedback;
        if (!feedback || typeof feedback !== 'object') return undefined;
        const value = feedback as {
          rating?: unknown;
          comment?: unknown;
          userId?: unknown;
          createdAt?: unknown;
        };
        if (value.rating !== 'good' && value.rating !== 'bad') return undefined;

        const result: {
          rating: 'good' | 'bad';
          comment?: string;
          userId?: string;
          createdAt?: string;
        } = { rating: value.rating };

        if (typeof value.comment === 'string' && value.comment.trim().length > 0) {
          result.comment = value.comment;
        }
        if (typeof value.userId === 'string' && value.userId.trim().length > 0) {
          result.userId = value.userId;
        }
        if (typeof value.createdAt === 'string' && value.createdAt.trim().length > 0) {
          result.createdAt = value.createdAt;
        }
        return result;
      },

      /**
       * The message's full text, concatenated across every text part.
       *
       * The server persists each streamed chunk as its own separate TextPart (`ServerChatMessageModel
       * .appendStreamingPart` never merges, unlike the client's own `appendPart`) — a Firestore-loaded
       * reply with multiple chunks has several TextParts, not one. Returning just the first (as this
       * used to) silently truncates a multi-chunk reply to its first chunk once it's read from
       * Firestore instead of the live optimistic copy.
       */
      get textContent(): string | undefined {
        return extractText(this.content);
      },

      /** Check if the message has any tool requests */
      get hasToolRequests(): boolean {
        return this.content.some((part) => isToolRequestPart(part));
      },

      get wellFormedErrors(): WellFormedError[] {
        return this.errors.filter(isWellFormedError);
      },

      get unknownErrors(): unknown[] {
        return this.errors.filter((error) => !isWellFormedError(error));
      },

      get errors(): unknown[] {
        return [this.metadata?.error, this.metadata?.errors, this.metadata?.cause].filter(Boolean);
      },

      /**
       * Appends a message part, merging consecutive text parts.
       * Wrapped in batch() to coalesce internal signal updates (text mutation + array access)
       * into a single React notification.
       */
      appendPart(newPart: MessagePart): void {
        batch(() => {
          const lastPart = this.content.at(-1);
          if (lastPart && isTextPart(lastPart) && isTextPart(newPart)) {
            if (shouldSeparateAfter(lastPart.text)) {
              lastPart.text += TextPartsSeparator;
            }
            lastPart.text += newPart.text;
          } else {
            this.content.push(newPart);
          }
        });
      },

      get status(): MessageStatus {
        return this.__optimisticState ?? 'completed';
      },

      get isStreaming(): boolean {
        return this.__optimisticState === 'streaming';
      },

      get isOptimistic(): boolean {
        return !!this.__optimisticState;
      },

      /**
       * True while the server is still generating this reply, derived from persisted metadata so it
       * survives a page reload (unlike the in-memory `isStreaming`). A flag left set by a hard
       * server crash is ignored once older than `STALE_GENERATION_MS`.
       */
      get isGenerating(): boolean {
        // Subscribe this computed getter to the timer-driven invalidation below. Date.now() alone is
        // not reactive, so without this tick a reply observed before the deadline stays generating
        // indefinitely until some unrelated Firestore update happens.
        void this.__generationExpiryTick;
        const meta = this.metadata as Record<string, unknown> | undefined;
        if (meta?.generating !== true) {
          const scheduled = generationExpiryTimers.get(this);
          if (scheduled) clearTimeout(scheduled.timer);
          generationExpiryTimers.delete(this);
          return false;
        }
        const startedAt = typeof meta.generatingStartedAt === 'number' ? meta.generatingStartedAt : undefined;
        if (startedAt === undefined) return true;

        const expiresAt = startedAt + STALE_GENERATION_MS;
        const remainingMs = expiresAt - Date.now();
        if (remainingMs <= 0) return false;

        const scheduled = generationExpiryTimers.get(this);
        if (scheduled?.expiresAt !== expiresAt) {
          if (scheduled) clearTimeout(scheduled.timer);
          const timer = setTimeout(() => {
            generationExpiryTimers.delete(this);
            this.__generationExpiryTick += 1;
          }, remainingMs);
          // Do not keep Node/Vitest alive solely for a client-side staleness timer.
          if (typeof timer === 'object' && 'unref' in timer && typeof timer.unref === 'function') timer.unref();
          generationExpiryTimers.set(this, { expiresAt, timer });
        }
        return true;
      },

      /** True when the user intentionally stopped this reply (persisted; survives reload). */
      get wasStopped(): boolean {
        return (this.metadata as Record<string, unknown> | undefined)?.stopped === true;
      },

      /**
       * How long the reply had been generating when the user stopped it, in ms. Read from
       * `metadata.generation.durationMs`, which the server's `finalizeStoppedReply` persists (start
       * of generation → stop). Undefined when the timing wasn't recorded, so callers fall back to a
       * duration-less label.
       */
      get stoppedDurationMs(): number | undefined {
        const generation = (this.metadata as { generation?: unknown } | undefined)?.generation;
        if (!generation || typeof generation !== 'object') return undefined;
        const durationMs = (generation as { durationMs?: unknown }).durationMs;
        return typeof durationMs === 'number' && Number.isFinite(durationMs) ? durationMs : undefined;
      },

      __optimisticState: undefined as MessageStatus | undefined,
      __pendingHydratedContent: undefined as MessagePart[] | undefined,
      __generationExpiryTick: 0,

      get answeredQuestions(): AnsweredQuestion[] {
        const parts = this.content.filter((part) => isAnsweredQuestionsPart(part));
        const answeredQuestions = parts.flatMap((part) =>
          Object.entries(part.data.answeredQuestions).map(([question, answer]) => ({ [question]: answer })),
        );
        return answeredQuestions;
      },
    }),
    {
      createMessage({ id = uuid(), createdAt = new Date(), updatedAt = createdAt, ...message }: NewMessage) {
        const path = ChatMessageModelSpec.path.toDocumentPath({ chatId: message.chatId, id });
        const ref = { id, path } as const;
        return model.converter.fromFireSnapshot({ id, ref, data: () => ({ ...message, createdAt, updatedAt }) });
      },
      createOptimisticUserMessage({ chatId, content, userId, status, sequence }: OptimisticMessageOpts) {
        const message = this.createMessage({ chatId, content, userId, role: 'user', sequence });
        message.__optimisticState = status;
        return message;
      },

      createOptimisticModelReplyMessage({
        chatId,
        replyTo,
        sequence,
        userId,
      }: {
        chatId: string;
        replyTo: string;
        sequence: number;
        userId: string;
      }) {
        const message = this.createMessage({ chatId, content: [], role: 'model', userId, replyTo, sequence });
        message.__optimisticState = 'receiving';
        return message;
      },
    },
    (data) => {
      const content = data.content ?? [];
      return { ...data, content: content.flatMap((part) => pipeline.processPartSync(part).parts) };
    },
  );
  return model;
}

export interface WellFormedError {
  message: string;
  cause?: unknown;
  name: string;
  stack: string;
}

export function isWellFormedError(error: unknown): error is WellFormedError {
  return typeof error === 'object' && error !== null && 'message' in error && 'name' in error && 'stack' in error;
}

function shouldSeparateAfter(partText: string) {
  return CanAddSeparatorAfterRegEx.test(partText.trim());
}

export type MessageStatus = 'sending' | 'receiving' | 'streaming' | 'completed' | 'failed';

/**
 * Optimistically rendered message before it is confirmed by the server.
 * TODO: Most likely will need to be either greatly extended or re-use the real message model.
 */
export interface OptimisticMessageOpts {
  chatId: string;
  userId: string;
  content: ClientMessagePart[];
  status: MessageStatus;
  sequence: number;
}

export type ChatMessageModel = ReturnType<typeof createChatMessageModel>;

export interface ChatMessage extends ClientInstanceOf<ChatMessageModel> {}

export interface ChatMessageInput extends InputOf<ChatMessageModelSpec> {}

export type NewMessage = Omit<ChatMessageData, 'id' | 'createdAt' | 'updatedAt'> & {
  id?: string;
  createdAt?: Date;
  updatedAt?: Date;
};
